export type QualityTier = "low" | "medium" | "high";

interface PerfState {
  tier: QualityTier;
  fps: number;
  reducedMotion: boolean;
}

const listeners = new Set<(s: PerfState) => void>();
let state: PerfState = { tier: "high", fps: 60, reducedMotion: false };

// Capture/diagnostic override: when localStorage `njrc.forceTier` is set to a
// valid tier, the governor pins that tier and skips fps-based downgrade. Used
// ONLY by the screenshot harness so headless (SwiftShader, ~low fps) captures
// render the real high-tier WebGL hero instead of the perf-safe static poster.
// Real users never set this, so production perf safety is unaffected.
function forcedTier(): QualityTier | null {
  if (typeof window === "undefined") return null;
  try {
    const v = window.localStorage?.getItem("njrc.forceTier");
    if (v === "low" || v === "medium" || v === "high") return v;
  } catch (e) { /* ignore */ }
  return null;
}

function deviceBaseTier(): QualityTier {
  if (typeof navigator === "undefined") return "high";
  const mem = (navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? 8;
  const cores = navigator.hardwareConcurrency ?? 8;
  const mobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  if (mem <= 3 || cores <= 4) return mobile ? "low" : "medium";
  if (mobile && (mem <= 4 || cores <= 6)) return "medium";
  return "high";
}

let started = false;
let frames = 0;
let lastSample = 0;
let rafId = 0;
let recovery = 0;
let mediaQuery: MediaQueryList | null = null;
let cleanupVisibility: (() => void) | null = null;
let cleanupMotion: (() => void) | null = null;

export function startPerfGovernor(): void {
  if (started || typeof window === "undefined") return;
  started = true;
  mediaQuery = window.matchMedia?.("(prefers-reduced-motion: reduce)") ?? null;
  const forced = forcedTier();
  state = {
    tier: forced ?? deviceBaseTier(),
    fps: 60,
    reducedMotion: mediaQuery?.matches ?? false,
  };
  emit();

  // Pinned tier (capture override): skip the fps-based governor entirely so the
  // tier never downgrades under a slow headless renderer.
  if (forced) {
    const onMotionForced = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
    mediaQuery?.addEventListener?.("change", onMotionForced);
    cleanupMotion = () => mediaQuery?.removeEventListener?.("change", onMotionForced);
    return;
  }

  lastSample = performance.now();
  const loop = () => {
    frames++;
    const now = performance.now();
    if (now - lastSample >= 1000) {
      const fps = (frames * 1000) / (now - lastSample);
      frames = 0;
      lastSample = now;
      let tier = state.tier;

      if (fps < 24 && tier !== "low") {
        tier = tier === "high" ? "medium" : "low";
        recovery = 0;
      } else if (fps >= 52 && tier !== "high") {
        recovery++;
        if (recovery >= 4) {
          tier = tier === "low" ? "medium" : "high";
          recovery = 0;
        }
      } else {
        recovery = 0;
      }

      if (Math.round(fps) !== state.fps || tier !== state.tier) {
        state = { ...state, fps: Math.round(fps), tier };
        emit();
      }
    }
    rafId = requestAnimationFrame(loop);
  };
  rafId = requestAnimationFrame(loop);

  const onVisibility = () => {
    if (!document.hidden) {
      frames = 0;
      lastSample = performance.now();
    }
  };
  document.addEventListener("visibilitychange", onVisibility);
  cleanupVisibility = () => document.removeEventListener("visibilitychange", onVisibility);

  const onMotion = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
  mediaQuery?.addEventListener?.("change", onMotion);
  cleanupMotion = () => mediaQuery?.removeEventListener?.("change", onMotion);
}

export function getPerf(): PerfState {
  return state;
}

export function onPerf(fn: (s: PerfState) => void): () => void {
  listeners.add(fn);
  fn(state);
  return () => listeners.delete(fn);
}

export function setReducedMotion(v: boolean): void {
  if (state.reducedMotion === v) return;
  state = { ...state, reducedMotion: v };
  emit();
}

function emit(): void {
  for (const fn of listeners) fn(state);
}

export const PARTICLE_BUDGET: Record<QualityTier, number> = { low: 0, medium: 90, high: 260 };
export const AURORA_SCALE: Record<QualityTier, number> = { low: 0.4, medium: 0.6, high: 0.8 };

export function stopPerfGovernor(): void {
  cancelAnimationFrame(rafId);
  cleanupVisibility?.();
  cleanupMotion?.();
  cleanupVisibility = null;
  cleanupMotion = null;
  mediaQuery = null;
  started = false;
}
